/**
 * Marks an element whose wheel gesture is handled by node-local UI instead of
 * WorkspacePageV2's document-level canvas zoom handler.
 */
export const CANVAS_LOCAL_WHEEL_TARGET_ATTR = 'data-canvas-local-wheel-target'
export const CANVAS_LOCAL_WHEEL_TARGET_SELECTOR = `[${CANVAS_LOCAL_WHEEL_TARGET_ATTR}="true"]`

type CanvasWheelZoomDecision =
  | { kind: 'ignore' }
  | { kind: 'skip-local-target' }
  | { kind: 'suppress-stale-meta' }
  | { kind: 'zoom'; overNowheel: boolean }

/**
 * Returns true when a wheel event target belongs to a node-local wheel surface.
 */
export function isCanvasLocalWheelTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(CANVAS_LOCAL_WHEEL_TARGET_SELECTOR))
}

/**
 * Decides whether WorkspacePageV2 should consume a wheel event for canvas zoom.
 */
export function getCanvasWheelZoomDecision({
  canvasElement,
  ctrlZoom,
  metaZoom,
  staleMeta,
  target,
  zoomGestureActive,
}: {
  canvasElement: Element | null
  ctrlZoom: boolean
  metaZoom: boolean
  staleMeta: boolean
  target: EventTarget | null
  zoomGestureActive: boolean
}): CanvasWheelZoomDecision {
  if (!(ctrlZoom || metaZoom || staleMeta)) return { kind: 'ignore' }
  if (!(target instanceof Element)) return { kind: 'ignore' }
  if (!canvasElement?.contains(target)) return { kind: 'ignore' }
  if (isCanvasLocalWheelTarget(target)) return { kind: 'skip-local-target' }
  if (!(ctrlZoom || metaZoom)) return { kind: 'suppress-stale-meta' }

  const overNowheel = Boolean(target.closest('.nowheel'))
  if (!overNowheel && !zoomGestureActive) return { kind: 'ignore' }
  return { kind: 'zoom', overNowheel }
}
